import type { ReactNode } from 'react';

/** Ein Balken des Signets: Lage und Grösse im `viewBox` 0 0 64 64, dazu die Füllklasse. */
export interface SignetBalken {
  readonly x: number;
  readonly y: number;
  readonly breite: number;
  readonly hoehe: number;
  readonly radius: number;
  /**
   * Die Füllung als Klasse der Tokenschicht (`fill-signet-n`, Farbwerte in
   * `src/styles.css`). Als vollständige Zeichenkette und nicht aus der Nummer
   * zusammengesetzt, damit Tailwind sie beim Durchsuchen findet.
   */
  readonly fuellung: 'fill-signet-1' | 'fill-signet-2' | 'fill-signet-3';
}

/**
 * Die Geometrie des Signets — die EINE Fassung im Browserbündel.
 *
 * Variante 1a „Konto-F" des Markenpakets (Stand 2026-10-14): Drei
 * Buchungszeilen ergeben ein F, oben die längste, unten die kürzeste. Reihenfolge,
 * Längen und Abstufung sind Markenregel und werden nicht verändert; der
 * Schutzraum rundum ist die Höhe eines Balkens (13/64 der Signethöhe).
 *
 * **Warum die Rechtecke hier als Konstante stehen und nicht in der
 * Komponente.** Das Startbild nach der Anmeldung
 * (`app/splash/SplashScreen.tsx`) zeichnet dieselben Balken, aber mit anderen
 * Attributen: Es lässt sie nacheinander von links wachsen. Es kann `FameSignet`
 * deshalb nicht verwenden — wohl aber dieselbe Geometrie, und genau darauf
 * kommt es an: Zwei abgeschriebene Rechteckangaben wären zwei Signets, von
 * denen eines bei der nächsten Markenauffrischung stehen bliebe (`E-1906`).
 */
export const FAME_SIGNET_BALKEN: readonly SignetBalken[] = [
  { x: 6, y: 6, breite: 52, hoehe: 13, radius: 2, fuellung: 'fill-signet-1' },
  { x: 6, y: 25.5, breite: 38, hoehe: 13, radius: 2, fuellung: 'fill-signet-2' },
  { x: 6, y: 45, breite: 15, hoehe: 13, radius: 2, fuellung: 'fill-signet-3' },
];

/**
 * Das Produktzeichen von FameAccounting.
 *
 * **Produkt, nicht Hersteller (`PF-signet`, Task 19 des Plans
 * Systemeinstellungen, `D-16`).** Das Zeichen trägt weder einen Herstellernamen
 * noch einen Schriftzug, und seine Beschriftung für Vorleseprogramme nennt das
 * PRODUKT. Der Name der Installation steht dort, wo er hingehört: in der
 * Fassung des Ausstellers (`PF-marke`).
 *
 * **Seit `PF-signetbalken` (2026-10-15) drei Balken statt des alten „F"**, wie
 * Lizenzserver und Kundenbereich es schon zeigen. Die Farbe kommt aus den
 * Rollen `--fa-signet-1` bis `--fa-signet-3` der Tokenschicht und schaltet
 * mit dem Farbschema zwischen der hellen und der dunklen Abstufung um —
 * anders als das frühere Zeichen folgt es NICHT `currentColor`: Drei Balken
 * mit drei Farben sind eine Marke, keine Glyphe, und eine einzige
 * Vererbungsfarbe hätte die Abstufung zerstört, die das Zeichen ausmacht.
 *
 * **Es ist kein `<img>`.** Als Bild geladen könnte die Datei die
 * Farbschemaumschaltung nicht lesen, und die Anmeldemaske holte für ein paar
 * hundert Byte einen zweiten Netzzugriff, bevor sie überhaupt etwas anzeigt.
 *
 * **`role="img"` mit `aria-label`.** Ein `<svg>` ohne beides ist für ein
 * Vorleseprogramm ein leerer Knoten; daneben steht zwar die Wortmarke als
 * Text, aber das Signet erscheint auch dort, wo sie es nicht tut.
 *
 * Die Grösse kommt ausschliesslich über `className` (`size-[34px]` auf der
 * Anmeldemaske); das `viewBox` ist quadratisch, das Signet skaliert damit
 * verzerrungsfrei. Mindestgrösse nach Markenregel: 16 px.
 */
export function FameSignet(props: { readonly className?: string }): ReactNode {
  return (
    <svg
      viewBox="0 0 64 64"
      role="img"
      aria-label="FameAccounting"
      {...(props.className === undefined ? {} : { className: props.className })}
    >
      {FAME_SIGNET_BALKEN.map((balken) => (
        <rect
          key={balken.y}
          x={balken.x}
          y={balken.y}
          width={balken.breite}
          height={balken.hoehe}
          rx={balken.radius}
          className={balken.fuellung}
        />
      ))}
    </svg>
  );
}
